/**
 * The per-aircraft `<PLANE>.DAT` values, transcribed from
 * `docs/engineering/flight-tunables.md` §7 — which is itself a straight read of
 * all 33 files, correlated against the manual's performance tables (field map
 * and evidence: `docs/formats/missions-campaign.md` § *Aircraft data*).
 *
 * **This file is a stand-in for a bake stage.** AD-007e puts the per-aircraft
 * spec in the bake pipeline's output; until that stage exists, the numbers live
 * here so the flight core is testable, and they are kept in the same shape a
 * generator would emit: raw file values only, with every derivation
 * (mass, wing area, drag polar, load limits) done in aircraft-spec.ts.
 *
 * Two things are *not* file reads and are marked where they appear:
 * `isRotary`, which has no field at all and comes from `docs/game/aircraft.md`
 * §9, and the airframe layout, which comes from the availability table in
 * flight-tunables.md §7.
 *
 * Deliberately **not** transcribed: the rest of the control block, offsets
 * 46 / 48 / 50 / 52. flight-tunables.md §2.2 shows they co-vary with control
 * response in a 7-class table and says outright "do not invent four independent
 * meanings" — which axis each drives is open question #13, and needs one
 * runtime session (change one, time a 360° roll and a 360° turn) to name them.
 * Until then the control model is driven by field 42 alone.
 *
 * The **gun block** (offsets 218+) lives in the sibling `aircraft-gun-table.ts`
 * rather than here, because it is variable-length: one 16-byte record per gun
 * station, and a two-seater has two. Its `+10` field is also where the gun
 * cone now comes from — the `FLEET_GUN_CONE_BAM = 500` / `ALBA_D5: 910`
 * exception pair this file used to carry was a hand-copy of that column.
 */

/**
 * One aircraft's raw fields, in `.DAT` offset order where possible. Kept as a
 * labelled tuple rather than 33 spelled-out objects so the whole fleet stays
 * reviewable as a table — the labels below *are* the column headings.
 */
export type AircraftRow = readonly [
  /** offset 108 ÷ 5 — service ceiling, exact. */
  ceilingFt: number,
  /** offset 32 — power rating. */
  power: number,
  /** offset 36 — weight/drag rating. */
  drag: number,
  /** offset 24 — stall/lift coefficient class: 650 / 700 / 800 / 1000. */
  stall: number,
  /** offset 42 — control response. */
  ctlResp: number,
  /** offset 54 — rudder yaw rate. */
  yawRate: number,
  /** offset 116 — turn performance, right. */
  turnRight: number,
  /** offset 118 — turn performance, left. */
  turnLeft: number,
  /** offset 72 — spin class: 0 never / 60 provoked / 50 fairly easily / 30 very easily. */
  spinClass: number,
  /** offset 104 — wing strength; **0 is the indestructible sentinel**, not the weakest. */
  wingStrength: number,
  /** NOT a file field — `docs/game/aircraft.md` §9. */
  rotary: boolean,
  /** offset 66 — rotary rolling torque (negative = right-rolling). */
  torqueRoll: number,
  /** offset 68 — the paired gyroscopic term. */
  torqueGyro: number,
  /** offset 56 — rotary coupling term, read as slipstream yaw. INFERRED. */
  rotaryCoupling: number,
  /** offset 216 — gun positions. */
  gunPositions: number,
  /** offset 206 — gun effectiveness (a damage multiplier, not a count). */
  gunDamage: number,
  /** offset 172 — collision size. */
  collisionSize: number,
];

export const AIRCRAFT_ROWS = {
  MORANE: [13000, 43, 40, 800, 250, 180, 330, 330, 0, 2900, true, -100, 50, -80, 1, 800, 100],
  FOKE3: [11500, 49, 44, 800, 250, 180, 360, 360, 0, 2900, true, -100, 50, -80, 1, 1024, 100],
  AIRCO: [14500, 45, 40, 650, 400, 180, 400, 400, 50, 2900, true, 0, 0, -100, 1, 800, 100],
  NIEU11: [15090, 44, 37, 700, 500, 180, 600, 600, 50, 2500, true, 0, 0, -60, 1, 1024, 100],
  NIEUPORT: [17400, 54, 40, 650, 400, 180, 600, 600, 50, 2500, true, -100, 50, -80, 1, 900, 100],
  SOPPUP: [17500, 44, 35, 650, 400, 180, 500, 500, 0, 3300, true, 0, 0, -80, 1, 1300, 100],
  SPAD_7: [18000, 55, 38, 650, 300, 180, 430, 430, 0, 3300, false, 0, 0, 0, 1, 800, 100],
  ALBA_D2: [17000, 56, 43, 650, 300, 180, 430, 430, 0, 2500, false, 0, 0, 0, 1, 1300, 100],
  SOPTRI: [20500, 52, 38, 700, 500, 180, 580, 580, 30, 3300, true, -250, 100, -100, 1, 800, 100],
  ALBA_D3: [18000, 56, 42, 700, 400, 180, 600, 600, 0, 2500, false, 0, 0, 0, 1, 1300, 100],
  HALBD2: [19600, 56, 51, 700, 400, 180, 430, 430, 50, 3300, false, 0, 0, -20, 1, 1024, 100],
  SE5: [19500, 72, 44, 650, 400, 180, 610, 610, 0, 0, false, 0, 0, 0, 1, 1300, 100],
  ALBA_D5: [20000, 64, 45, 650, 450, 180, 630, 630, 0, 2900, false, 0, 0, 0, 1, 1300, 100],
  CAMEL: [19000, 56, 40, 700, 600, 180, 930, 700, 30, 3300, true, -400, 200, -130, 1, 1300, 100],
  PFALZ: [17000, 60, 47, 650, 400, 180, 540, 540, 0, 3300, false, 0, 0, 0, 1, 1300, 100],
  FOKDR1: [20000, 49, 37, 1000, 650, 600, 780, 780, 30, 2900, true, -200, 100, -100, 1, 1300, 100],
  SPAD_13: [21800, 77, 48, 650, 450, 180, 650, 650, 0, 0, false, 0, 0, 0, 1, 1300, 100],
  NIEU28: [17000, 72, 48, 800, 450, 180, 700, 700, 60, 2900, true, 0, 0, -80, 1, 800, 100],
  FOKD7: [23000, 82, 56, 700, 500, 180, 700, 700, 0, 0, false, 0, 0, 0, 1, 1300, 100],
  SIEMENS: [26240, 72, 52, 1000, 650, 180, 750, 750, 30, 3300, true, -200, 100, -100, 1, 1300, 100],
  SNIPE: [20500, 72, 47, 700, 600, 180, 840, 780, 60, 3300, true, -200, 100, -80, 1, 1300, 100],
  FOKD8: [19680, 75, 49, 800, 500, 180, 720, 720, 0, 2200, true, 0, 0, 0, 1, 1300, 100],
  BE2: [10000, 54, 50, 700, 250, 180, 450, 450, 0, 2900, false, 0, 0, 0, 1, 800, 180],
  AVIATIK: [11500, 54, 50, 700, 250, 180, 450, 450, 0, 2900, false, 0, 0, 0, 1, 800, 180],
  FE2: [11000, 54, 50, 700, 250, 180, 450, 450, 0, 2900, false, 0, 0, 0, 2, 800, 180],
  ROLAND: [20000, 55, 45, 700, 250, 180, 450, 450, 0, 2900, false, 0, 0, 0, 2, 800, 180],
  RE8: [13500, 55, 45, 700, 250, 180, 450, 450, 0, 2900, false, 0, 0, 0, 2, 800, 180],
  RUMPLER: [21000, 57, 45, 700, 250, 180, 450, 450, 0, 2900, false, 0, 0, 0, 2, 800, 180],
  BRISTOL: [20000, 67, 45, 650, 400, 180, 500, 500, 0, 0, false, 0, 0, 0, 2, 1300, 100],
  DH4: [16000, 58, 50, 700, 250, 180, 450, 450, 0, 2900, false, 0, 0, 0, 2, 800, 180],
  JUNKER: [20000, 58, 50, 700, 250, 180, 450, 450, 0, 2900, false, 0, 0, 0, 2, 800, 180],
  GOTHA: [21300, 58, 50, 700, 250, 180, 400, 400, 0, 0, false, 0, 0, 0, 3, 1024, 300],
  HANDLEY: [7000, 58, 50, 700, 250, 180, 400, 400, 0, 0, false, 0, 0, 0, 3, 1300, 300],
} satisfies Record<string, AircraftRow>;

export type AircraftKey = keyof typeof AIRCRAFT_ROWS;

/** The same row with its columns named, which is how everything downstream reads it. */
export interface AircraftFields {
  readonly ceilingFt: number;
  readonly power: number;
  readonly drag: number;
  readonly stall: number;
  readonly ctlResp: number;
  readonly yawRate: number;
  readonly turnRight: number;
  readonly turnLeft: number;
  readonly spinClass: number;
  readonly wingStrength: number;
  readonly rotary: boolean;
  readonly torqueRoll: number;
  readonly torqueGyro: number;
  readonly rotaryCoupling: number;
  readonly gunPositions: number;
  readonly gunDamage: number;
  readonly collisionSize: number;
}

export function fieldsOf(key: AircraftKey): AircraftFields {
  const row: AircraftRow = AIRCRAFT_ROWS[key];
  return {
    ceilingFt: row[0],
    power: row[1],
    drag: row[2],
    stall: row[3],
    ctlResp: row[4],
    yawRate: row[5],
    turnRight: row[6],
    turnLeft: row[7],
    spinClass: row[8],
    wingStrength: row[9],
    rotary: row[10],
    torqueRoll: row[11],
    torqueGyro: row[12],
    rotaryCoupling: row[13],
    gunPositions: row[14],
    gunDamage: row[15],
    collisionSize: row[16],
  };
}

export const AIRCRAFT_KEYS: readonly AircraftKey[] = Object.keys(AIRCRAFT_ROWS).filter(
  (key): key is AircraftKey => key in AIRCRAFT_ROWS,
);

/** Airframe layout, from the availability table in flight-tunables.md §7 (F.E.2b's pusher layout from aircraft.md). */
export type AirframeConfig = "biplane" | "triplane" | "monoplane" | "pusher";

const AIRFRAME_EXCEPTIONS: Partial<Record<AircraftKey, AirframeConfig>> = {
  MORANE: "monoplane",
  FOKE3: "monoplane",
  FOKD8: "monoplane",
  SOPTRI: "triplane",
  FOKDR1: "triplane",
  AIRCO: "pusher",
  FE2: "pusher",
};

export function airframeOf(key: AircraftKey): AirframeConfig {
  return AIRFRAME_EXCEPTIONS[key] ?? "biplane";
}

export const DISPLAY_NAMES: Readonly<Record<AircraftKey, string>> = {
  MORANE: "Morane-Saulnier N Bullet",
  FOKE3: "Fokker E.III Eindecker",
  AIRCO: "Airco D.H.2",
  NIEU11: "Nieuport 11 Bébé",
  NIEUPORT: "Nieuport 17",
  SOPPUP: "Sopwith Pup",
  SPAD_7: "SPAD VII",
  ALBA_D2: "Albatros D.II",
  SOPTRI: "Sopwith Triplane",
  ALBA_D3: "Albatros D.III",
  HALBD2: "Halberstadt D.II",
  SE5: "S.E.5a",
  ALBA_D5: "Albatros D.Va",
  CAMEL: "Sopwith Camel",
  PFALZ: "Pfalz D.III",
  FOKDR1: "Fokker Dr.I",
  SPAD_13: "SPAD XIII",
  NIEU28: "Nieuport 28",
  FOKD7: "Fokker D.VII",
  SIEMENS: "Siemens-Schuckert D.III",
  SNIPE: "Sopwith Snipe",
  FOKD8: "Fokker D.VIII",
  BE2: "B.E.2c",
  AVIATIK: "Aviatik C.I",
  FE2: "F.E.2b",
  ROLAND: "Roland C.II",
  RE8: "R.E.8",
  RUMPLER: "Rumpler C.IV",
  BRISTOL: "Bristol F.2B",
  DH4: "Airco D.H.4",
  JUNKER: "Junkers J.I",
  GOTHA: "Gotha G.IV",
  HANDLEY: "Handley Page O/400",
};

/** offset 26, rudder authority under load: 120 fleet-wide, 140 on the Bristol alone (§2.4). */
export const FLEET_RUDDER_AUTHORITY = 120;
const RUDDER_AUTHORITY_EXCEPTIONS: Partial<Record<AircraftKey, number>> = { BRISTOL: 140 };

/**
 * offset 208: 200 on every one of the 33 files (§6). Carried under this name
 * for the existing calibration that hangs off it, but AD-011 §4.1
 * (`gun-mounts-design.md`) found no `mg.c` code reading this field — the game's
 * real jam table lives at 210/212/214 instead. See `FLEET_GUN_JAM_TABLE`.
 */
export const FLEET_JAM_THRESHOLD = 200;

/**
 * offsets 210/212/214, `[40, 20, 30]` on every one of the 33 files
 * (`packages/formats`' `plane-dat.test.ts`) — `mg.c`'s real three-entry jam
 * table (AD-011 §4.1), read at `mg.c:0x44b` and selected by a per-shot field
 * (`aircraft[+0x6a]`) that field's own "read by" column in AD-011's static
 * trace does not resolve. There is no per-aircraft variant of this table to
 * transcribe — every file carries the same three numbers — only the selector
 * is missing, so `GunSpec.jamThresholdTable` carries it as reference data
 * rather than driving `jamProbability` on a guessed index (`flight/guns.ts`).
 */
export const FLEET_GUN_JAM_TABLE: readonly [number, number, number] = [40, 20, 30];

/** offset 140, target size: 120 fleet-wide, 300 for the two heavy bombers. */
export const FLEET_TARGET_SIZE = 120;
const TARGET_SIZE_EXCEPTIONS: Partial<Record<AircraftKey, number>> = { GOTHA: 300, HANDLEY: 300 };

export function rudderAuthorityOf(key: AircraftKey): number {
  return RUDDER_AUTHORITY_EXCEPTIONS[key] ?? FLEET_RUDDER_AUTHORITY;
}

export function targetSizeOf(key: AircraftKey): number {
  return TARGET_SIZE_EXCEPTIONS[key] ?? FLEET_TARGET_SIZE;
}

/**
 * The nine AI-only types that ship with **copy-pasted (power, drag) class
 * defaults** — `(54,50)`, `(55,45)`, `(58,50)` — so the ratio law reproduces
 * what the game flies, not what the manual claims (flight-tunables.md §1.1
 * asks for this to be a documented switch rather than an accident).
 */
export const SHARED_PERFORMANCE_CLASS: readonly AircraftKey[] = [
  "AVIATIK",
  "BE2",
  "FE2",
  "RE8",
  "ROLAND",
  "DH4",
  "GOTHA",
  "HANDLEY",
  "JUNKER",
];

/**
 * The manual's own max-speed figures, for cross-checking the derived envelope.
 * The five expansion aircraft (D.VIII, Halberstadt, Nieuport 11, Nieuport 28,
 * Siemens-Schuckert) have no manual entry and are absent here.
 */
export const MANUAL_MAX_SPEED_MPH: Partial<Record<AircraftKey, number>> = {
  SE5: 138,
  SPAD_13: 133,
  BRISTOL: 125,
  SNIPE: 121,
  SPAD_7: 119,
  DH4: 117,
  ALBA_D5: 116,
  CAMEL: 115,
  FOKD7: 114,
  SOPTRI: 113,
  NIEUPORT: 110,
  ALBA_D3: 109,
  ALBA_D2: 109,
  FOKDR1: 103,
  RE8: 103,
  ROLAND: 103,
  PFALZ: 102,
  SOPPUP: 99,
  JUNKER: 97,
  AIRCO: 93,
  FE2: 91,
  MORANE: 90,
  AVIATIK: 89,
  FOKE3: 88,
  GOTHA: 87,
  HANDLEY: 85,
  BE2: 72,
  RUMPLER: 106,
};
